import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";
import { isEqual } from "lodash";
import { searchAssets } from "../../../../utils/API/Assets/Assets";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import GoogleMapComponent from "../../../../components/Maps/GoogleMapComponent";
import PopUpMarkerContent from "../../../../components/Maps/PopUpMarkerContent";

const STORAGE_KEY_CENTER = "atlas_dashboard_map_center";
const STORAGE_KEY_ZOOM = "atlas_dashboard_map_zoom";
const STORAGE_KEY_MAP_TYPE = "atlas_dashboard_map_type";
const MS_24H = 24 * 60 * 60 * 1000;
const MS_7D = 7 * 24 * 60 * 60 * 1000;

/**
 * Compact MVP Map Widget for Dashboard
 * Uses the same logic as MVP-Assets but in a smaller, widget-friendly format
 */
export default function MVPMapWidget(props) {
  const { timeZone } = props;
  const history = useHistory();
  const theme = useTheme();
  const dispatchGlobal = useDispatch();
  const { facilities } = useSelector((state) => state.organization, isEqual);
  const zones = useSelector((state) => state.organization.zones, isEqual);

  const [state, setState] = useState({
    markersToDisplay: [],
    selectedAssetId: "",
    selectedAsset: {},
  });
  const { markersToDisplay, selectedAssetId, selectedAsset } = state;

  const [activeMarker, setActiveMarker] = useState(null);
  const [facilityFilter, setFacilityFilter] = useState("");
  const [timeRangeFilter, setTimeRangeFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");

  const loadSavedMapPrefs = useCallback(() => {
    try {
      const savedCenter = localStorage.getItem(STORAGE_KEY_CENTER);
      const savedZoom = localStorage.getItem(STORAGE_KEY_ZOOM);
      const savedMapType = localStorage.getItem(STORAGE_KEY_MAP_TYPE);
      const center = savedCenter ? JSON.parse(savedCenter) : null;
      const zoom = savedZoom != null ? parseInt(savedZoom, 10) : null;
      const mapTypeId = savedMapType || null;
      if (
        center &&
        Number.isFinite(center.lat) &&
        Number.isFinite(center.lng) &&
        Number.isFinite(zoom) &&
        zoom >= 0 &&
        zoom <= 22
      ) {
        return {
          center: { lat: center.lat, lng: center.lng },
          zoom,
          mapTypeId: mapTypeId === "roadmap" || mapTypeId === "satellite" || mapTypeId === "hybrid" || mapTypeId === "terrain" ? mapTypeId : "satellite",
        };
      }
    } catch (_) {}
    return null;
  }, []);

  const [mapSettings, setMapSettings] = useState(() => {
    const saved = loadSavedMapPrefs();
    if (saved) return saved;
    return {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeId: "satellite",
    };
  });

  const [userLocation, setUserLocation] = useState(null);
  const isMountedRef = useRef(false);
  const mapRef = useRef(null);

  // Brand colors from theme
  const { palette = {} } = theme;
  const { primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const greenLight = isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.08)";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");

  // Convert payload to markers (filter out zone-based items)
  const convertPayloadToMarkers = (payload) => {
    return payload
      ?.map((event) => {
        if (event.zone && event.zone.zoneId) {
          return null;
        } else if (
          event &&
          (event.latitude !== 0 || event.longitude !== 0) &&
          event.latitude &&
          event.longitude
        ) {
          return event;
        } else if (event && event.facilityId) {
          const currentFacility = facilities[event.facilityId] || {};
          const { location = {} } = currentFacility;
          const { latitude, longitude } = location;
          if (latitude && longitude) {
            return { ...event, latitude, longitude };
          }
        }
        return null;
      })
      .filter((event) => event !== null);
  };

  // Persist map preferences to localStorage
  useEffect(() => {
    if (!mapSettings) return;
    try {
      if (mapSettings.center && Number.isFinite(mapSettings.center.lat) && Number.isFinite(mapSettings.center.lng)) {
        localStorage.setItem(STORAGE_KEY_CENTER, JSON.stringify(mapSettings.center));
      }
      if (Number.isFinite(mapSettings.zoom)) {
        localStorage.setItem(STORAGE_KEY_ZOOM, String(mapSettings.zoom));
      }
      if (mapSettings.mapTypeId) {
        localStorage.setItem(STORAGE_KEY_MAP_TYPE, mapSettings.mapTypeId);
      }
    } catch (_) {}
  }, [mapSettings]);

  // Find and center map on assets (only when no saved prefs)
  const findAndCenterMap = (assetsToConsider) => {
    try {
      if (localStorage.getItem(STORAGE_KEY_CENTER)) return;
    } catch (_) {}
    if (!assetsToConsider || assetsToConsider.length === 0) {
      setMapSettings((prev) => ({
        ...prev,
        center: { lat: 39.8283, lng: -98.5795 },
        zoom: 4,
        mapTypeId: prev?.mapTypeId || "satellite",
      }));
      return;
    }

    const suitableAssets = assetsToConsider.filter(
      (asset) => asset.latitude && asset.longitude && !asset.zone
    );

    if (suitableAssets.length > 0) {
      const firstAsset = suitableAssets[0];
      setMapSettings((prev) => ({
        ...prev,
        center: { lat: firstAsset.latitude, lng: firstAsset.longitude },
        zoom: 5,
        mapTypeId: prev?.mapTypeId || "satellite",
      }));
    }
  };

  // Filter by facility then by time (last event: timeOfLogLong, timeStamp, lastSeen)
  const filteredMarkers = useMemo(() => {
    let list = markersToDisplay;
    if (facilityFilter) {
      list = list.filter((m) => m.facilityId === facilityFilter);
    }
    if (timeRangeFilter === "all") return list;
    const now = Date.now();
    const maxAge = timeRangeFilter === "24h" ? MS_24H : MS_7D;
    return list.filter((m) => {
      const t = m.timeOfLogLong ?? m.timeStamp ?? m.lastSeen;
      if (t == null) return false;
      const ts = typeof t === "number" ? t : new Date(t).getTime();
      return now - ts <= maxAge;
    });
  }, [markersToDisplay, facilityFilter, timeRangeFilter]);

  // Search options: assets by tag, assetTag, or assetId
  const searchOptions = useMemo(() => {
    const q = (searchInput || "").trim().toLowerCase();
    if (!q) return [];
    return filteredMarkers.filter(
      (m) =>
        (m.tag && String(m.tag).toLowerCase().includes(q)) ||
        (m.assetTag && String(m.assetTag).toLowerCase().includes(q)) ||
        (m.assetId && String(m.assetId).toLowerCase().includes(q))
    );
  }, [filteredMarkers, searchInput]);

  const handleShowAll = useCallback(() => {
    if (mapRef.current?.fitAllMarkers) mapRef.current.fitAllMarkers();
  }, []);

  const handleSearchSelect = useCallback((asset) => {
    if (!asset?.latitude || !asset?.longitude) return;
    setMapSettings((prev) => ({
      ...prev,
      center: { lat: asset.latitude, lng: asset.longitude },
      zoom: 15,
    }));
    setActiveMarker(asset.assetId ?? null);
    setState((prev) => ({
      ...prev,
      selectedAssetId: asset.assetId ?? "",
      selectedAsset: asset,
    }));
    setSearchInput(asset.tag || asset.assetTag || "");
  }, []);

  // Handle view button click
  const handleViewButtonClick = () => {
    if (selectedAssetId) {
      history.push(`/assetSnapshot/${selectedAssetId}`, { from: "/" });
    } else if (activeMarker) {
      history.push(`/assetSnapshot/${activeMarker}`, { from: "/" });
    }
  };

  // Popup content (asset marker)
  const popupContent = (popupData, opts = {}) => {
    return (
      <PopUpMarkerContent
        buttonLabel="View Asset Snapshot"
        item={popupData}
        onClose={opts?.onClose}
        onButtonClick={handleViewButtonClick}
        onNavigateButtonClick={(item) => {
          const { latitude, longitude } = item || {};
          if (latitude && longitude) {
            if (userLocation) {
              // Navigation would go here
            } else if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  });
                },
                () => setUserLocation(null)
              );
            }
          }
        }}
      />
    );
  };

  // Facility popup content
  const facilityPopUpContent = (popupData, opts = {}) => {
    const facilityData = facilities[popupData?.facilityId] || {};

    return (
      <PopUpMarkerContent
        buttonLabel="View Facility"
        clickedFacility={true}
        facility={facilityData}
        item={selectedAsset}
        onClose={opts?.onClose}
        onButtonClick={() => {
          if (facilityData?.facilityId) {
            history.push(`/facilities/${facilityData.facilityId}`);
          }
        }}
        onButtonClick2={() => {
          if (selectedAssetId) {
            history.push(`/assetSnapshot/${selectedAssetId}`, { from: "/" });
          }
        }}
        onNavigateButtonClick={(facility) => {
          const { location = {} } = facilityData;
          const { latitude, longitude } = location;
          if (latitude && longitude) {
            setMapSettings({
              center: { lat: latitude, lng: longitude },
              zoom: 15,
              mapTypeId: "satellite",
            });
          }
        }}
      />
    );
  };

  // Fetch initial data
  useEffect(() => {
    if (!isMountedRef.current) {
      searchAssets(props, { limit: 5000 }).then((res) => {
        const { assets: allAssets = [], success = false } = res || {};
        if (success) {
          const markers = convertPayloadToMarkers(allAssets);
          setState((prevState) => ({
            ...prevState,
            markersToDisplay: markers,
          }));
          findAndCenterMap(allAssets);
        }
        isMountedRef.current = true;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.06)",
        border: `1px solid ${borderColor}`,
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: isDark
            ? `linear-gradient(90deg, ${greenColor} 0%, ${navyColor} 100%)`
            : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      {/* Map Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          background: "transparent",
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "10px",
              backgroundColor: greenLight,
            }}
          >
            <MapIcon sx={{ color: greenColor, fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? greenColor : navyColor,
                fontSize: "1rem",
                lineHeight: 1.2,
              }}
            >
              Asset Location
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: textSecondary, fontSize: "0.75rem" }}
            >
              {filteredMarkers.length} assets mapped
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Toolbar: premium filters, search, Show all */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: "10px 16px 12px",
          borderBottom: `1px solid ${borderColor}`,
          background: isDark
            ? "linear-gradient(180deg, rgba(0,40,67,0.06) 0%, rgba(0,0,0,0.04) 100%)"
            : "linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.6) 100%)",
          boxShadow: isDark ? "inset 0 1px 0 rgba(255,255,255,0.03)" : "inset 0 1px 0 rgba(255,255,255,0.8)",
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewComfyIcon sx={{ fontSize: "1rem" }} />}
          onClick={handleShowAll}
          sx={{
            textTransform: "none",
            fontSize: "0.8125rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
            height: 36,
            px: 1.5,
            borderRadius: "10px",
            borderColor: isDark ? "rgba(148,163,184,0.25)" : "rgba(0,40,67,0.15)",
            color: textPrimary,
            backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.2)" : "0 1px 2px rgba(0,0,0,0.04)",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: greenColor,
              color: greenColor,
              backgroundColor: isDark ? "rgba(160,196,109,0.12)" : "rgba(160,196,109,0.08)",
              boxShadow: isDark ? `0 2px 8px ${greenColor}25` : `0 2px 8px ${greenColor}20`,
            },
            "&:focus-visible": {
              outline: `2px solid ${greenColor}`,
              outlineOffset: 2,
            },
          }}
        >
          Show all
        </Button>
        <FormControl
          size="small"
          sx={{
            minWidth: 150,
            "& .MuiOutlinedInput-root": {
              height: 36,
              borderRadius: "10px",
              fontSize: "0.8125rem",
              fontWeight: 500,
              backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)",
              "& fieldset": {
                borderColor: isDark ? "rgba(148,163,184,0.2)" : "rgba(0,40,67,0.12)",
              },
              "&:hover fieldset": {
                borderColor: isDark ? "rgba(148,163,184,0.35)" : "rgba(0,40,67,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderWidth: "1.5px",
                borderColor: `${greenColor} !important`,
                boxShadow: `0 0 0 3px ${greenColor}20`,
              },
            },
            "& .MuiInputLabel-root": { fontSize: "0.75rem", fontWeight: 600 },
          }}
        >
          <InputLabel id="dashboard-map-facility-label">Facility</InputLabel>
          <Select
            labelId="dashboard-map-facility-label"
            value={facilityFilter}
            label="Facility"
            onChange={(e) => setFacilityFilter(e.target.value || "")}
          >
            <MenuItem value="">All facilities</MenuItem>
            {Object.values(facilities || {}).map((f) => (
              <MenuItem key={f.facilityId} value={f.facilityId}>
                {f.name || f.facilityId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{
            minWidth: 130,
            "& .MuiOutlinedInput-root": {
              height: 36,
              borderRadius: "10px",
              fontSize: "0.8125rem",
              fontWeight: 500,
              backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)",
              "& fieldset": {
                borderColor: isDark ? "rgba(148,163,184,0.2)" : "rgba(0,40,67,0.12)",
              },
              "&:hover fieldset": {
                borderColor: isDark ? "rgba(148,163,184,0.35)" : "rgba(0,40,67,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderWidth: "1.5px",
                borderColor: `${greenColor} !important`,
                boxShadow: `0 0 0 3px ${greenColor}20`,
              },
            },
            "& .MuiInputLabel-root": { fontSize: "0.75rem", fontWeight: 600 },
          }}
        >
          <InputLabel id="dashboard-map-time-label">Time</InputLabel>
          <Select
            labelId="dashboard-map-time-label"
            value={timeRangeFilter}
            label="Time"
            onChange={(e) => setTimeRangeFilter(e.target.value)}
          >
            <MenuItem value="all">All time</MenuItem>
            <MenuItem value="24h">Last 24h</MenuItem>
            <MenuItem value="7d">Last 7 days</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          size="small"
          options={searchOptions}
          getOptionLabel={(opt) => opt.tag || opt.assetTag || String(opt.assetId || "")}
          inputValue={searchInput}
          onInputChange={(_, v) => setSearchInput(v)}
          onChange={(_, value) => value && handleSearchSelect(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search asset"
              placeholder="Tag or ID"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 36,
                  borderRadius: "10px",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)",
                  "& fieldset": {
                    borderColor: isDark ? "rgba(148,163,184,0.2)" : "rgba(0,40,67,0.12)",
                  },
                  "&:hover fieldset": {
                    borderColor: isDark ? "rgba(148,163,184,0.35)" : "rgba(0,40,67,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: "1.5px",
                    borderColor: `${greenColor} !important`,
                    boxShadow: `0 0 0 3px ${greenColor}20`,
                  },
                },
                "& .MuiInputLabel-root": { fontSize: "0.75rem", fontWeight: 600 },
              }}
            />
          )}
          sx={{ minWidth: 170, flex: 1, maxWidth: 240 }}
        />
      </Box>

      {/* Map Container + Legend */}
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <GoogleMapComponent
          ref={mapRef}
          activeMarker={activeMarker}
          facilities={Object.values(facilities || {})}
          facilityPopUpContent={facilityPopUpContent}
          mapSettings={mapSettings}
          markerKey="assetId"
          markersToRender={filteredMarkers}
          popUpContent={popupContent}
          setActiveMarker={(value) => {
            setActiveMarker(value);
            const asset = filteredMarkers.find((m) => m.assetId === value) || markersToDisplay.find((m) => m.assetId === value);
            if (asset) {
              setState((prevState) => ({
                ...prevState,
                selectedAsset: asset,
                selectedAssetId: asset.assetId,
              }));
            }
          }}
          setMapSettings={setMapSettings}
        />
        {/* Legend */}
        <Box
          className="map-journey-legend"
          sx={{
            position: "absolute",
            bottom: 44,
            left: 12,
            right: "auto",
            maxWidth: 220,
            display: "flex",
            flexWrap: "wrap",
            gap: "6px 12px",
            alignItems: "center",
            padding: "5px 8px",
            background: isDark ? "rgba(3, 45, 66, 0.95)" : "rgba(255, 255, 255, 0.94)",
            borderRadius: 1,
            boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.3)" : "0 1px 8px rgba(0,0,0,0.08)",
            fontSize: "9px",
            fontWeight: 500,
            color: isDark ? "#f1f5f9" : "#1e293b",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#A0C46D",
                border: "1px solid #fff",
                boxShadow: "0 0 2px rgba(0,0,0,0.2)",
              }}
            />
            Asset
          </Box>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#3b82f6",
              }}
            />
            Facility
          </Box>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component="span"
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                bgcolor: "#A0C46D",
                border: "2px solid #002843",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 8,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              n
            </Box>
            Cluster
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
